import { readRaw, writeRaw, removeRaw } from './persistentStore';

/**
 * Remember the last page an operator was on, so returning to Aether resumes
 * where they left off instead of always dropping them on Competitions.
 *
 * Aether has no login of its own — a session always arrives from the OPUS
 * dashboard and lands on "/", which redirects. That redirect is the only reason
 * the route is lost, and it is where this is read.
 *
 * Two rules keep this from becoming a liability:
 *
 *   VALIDATE BEFORE RESTORING. A stored path is data from a previous build. If
 *   the route has since been removed, restoring it lands the operator on a 404
 *   with no idea why — strictly worse than the default. Only paths matching a
 *   known route pattern are restored.
 *
 *   NEVER RESTORE ACROSS USERS. Operators share machines. The stored route is
 *   tied to the user id that stored it and is discarded when that changes,
 *   because "where you were" can disclose which fixture someone was working on.
 */

const KEY = 'aether_last_route';

/**
 * Restorable route shapes, mirroring AppRoutes.
 *
 * Deliberately NOT restorable:
 *   "/"            the redirect that sent us here — restoring it would loop
 *   "/custom-stats" super-admin only; a demoted user would bounce on arrival
 * Anything unlisted (including a 404) simply falls back to the default.
 */
const RESTORABLE = [
  /^\/competitions\/?$/,
  /^\/seasons\/[^/]+\/?$/,
  /^\/fixtures\/[^/]+\/[^/]+\/?$/,
  /^\/all-fixtures\/?$/,
  /^\/teamsetup\/[^/]+\/[^/]+\/[^/]+\/?$/,
  /^\/collections\/[^/]+\/?$/,
  /^\/standings\/[^/]+\/[^/]+\/?$/,
  /^\/formations\/?$/,
  /^\/monitoring\/?$/,
  /^\/my-watchlist\/?$/,
];

export const DEFAULT_ROUTE = '/competitions';

export function isRestorableRoute(path) {
  if (typeof path !== 'string' || !path.startsWith('/')) return false;
  const [pathname] = path.split('?');
  return RESTORABLE.some((re) => re.test(pathname));
}

/** Remember `pathname + search`. Silently ignores anything not restorable. */
export function rememberRoute(path, userId) {
  if (!isRestorableRoute(path)) return;
  writeRaw(KEY, JSON.stringify({ path, userId: userId ?? null }));
}

/**
 * The route to land on. Falls back to the default whenever the stored value is
 * missing, unparseable, no longer a real route, or belongs to another user.
 */
export function readLastRoute(userId) {
  const raw = readRaw(KEY);
  if (!raw) return DEFAULT_ROUTE;
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return DEFAULT_ROUTE;
  }
  if (!parsed || typeof parsed.path !== 'string') return DEFAULT_ROUTE;
  // A null stored userId is from a session that never had one; only restore it
  // when the current session is equally anonymous.
  if ((parsed.userId ?? null) !== (userId ?? null)) return DEFAULT_ROUTE;
  return isRestorableRoute(parsed.path) ? parsed.path : DEFAULT_ROUTE;
}

export function forgetRoute() {
  removeRaw(KEY);
}
